import { Link, useRouterState } from '@tanstack/react-router'
import { Plus, Sparkles, X } from 'lucide-react'
import { useState } from 'react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'

const ACTIONS = [
  { to: '/dashboard/events/create', label: m.createEvent, icon: Plus },
  { to: '/dashboard/ai-studio/event-creator', label: m.quickCreateWithAi, icon: Sparkles },
] as const

const PAGES_WITH_OWN_CREATE = ['/dashboard/events', '/dashboard/ai-studio']
const PLACE_DETAIL = /^\/dashboard\/listings\/(?!create$)[^/]+$/

/** The event pages, a place page and the AI studio carry their own create entry, so the floating one would repeat it. */
export function hasOwnCreateButton(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '')
  if (PLACE_DETAIL.test(path)) return true
  return PAGES_WITH_OWN_CREATE.some((page) => path === page || path.startsWith(`${page}/`))
}

export function QuickCreateFab() {
  const [open, setOpen] = useState(false)
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  if (hasOwnCreateButton(pathname)) return null

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover must span button and actions as one surface - per-child handlers close the menu as the pointer crosses the gap; keyboard and touch reach the same state through the button
    <div
      className="fixed right-4 bottom-4 z-30 flex flex-col-reverse items-end gap-2 sm:right-6 sm:bottom-6"
      onPointerEnter={(e) => {
        if (e.pointerType === 'mouse') setOpen(true)
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse') setOpen(false)
      }}
      // Fires as focusout, so it also covers focus leaving any of the actions.
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false)
      }}
    >
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={open ? m.quickCreateClose() : m.quickCreateLabel()}
        onClick={() => setOpen((v) => !v)}
        className="flex size-14 items-center justify-center rounded-full border border-brand bg-brand text-brand-foreground outline-none transition-transform focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:scale-105 active:scale-95"
      >
        {open ? <X className="size-6" /> : <Plus className="size-6" />}
      </button>

      {open &&
        ACTIONS.map(({ to, label, icon: Icon }, index) => (
          <Link
            key={to}
            to={to}
            onClick={() => setOpen(false)}
            className={cn(
              'flex items-center gap-2 rounded-full border bg-card py-2 pr-4 pl-3 font-medium text-sm outline-none',
              'animate-in fade-in-0 slide-in-from-bottom-2 focus-visible:ring-2 focus-visible:ring-ring hover:bg-accent',
            )}
            style={{ animationDelay: `${index * 40}ms`, animationFillMode: 'backwards' }}
          >
            <Icon className="size-4 text-brand" />
            {label()}
          </Link>
        ))}
    </div>
  )
}
